import { Link } from "react-router-dom";
import {
  Radar, Layers, Copy, BellRing, GitCompareArrows, ShieldCheck,
  ArrowRight, MapPin, Check, Sparkles, MessageSquareQuote, History,
  Star, MessageCircle, Lock,
} from "lucide-react";

// Discreet, abstract hero backdrop (no people) — heavily blurred + darkened.
const HERO_IMG =
  "https://images.unsplash.com/photo-1647101734210-ff5d79813069?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

// Sites currently monitored (brand colours mirror backend scrapers.SOURCES)
const SITES = [
  { name: "Apartado X", color: "#c8aa6e" },
  { name: "Gostosas.pt", color: "#e4e4e7" },
  { name: "Mulheres VIP", color: "#a1a1aa" },
  { name: "Classificados X", color: "#9ca3af" },
  { name: "Portuas", color: "#6b7280" },
  { name: "Só Massagens", color: "#7dd3fc" },
  { name: "Doce Massagem", color: "#f0abfc" },
];

// The 10 core advantages, benefit-led and specific.
const ADVANTAGES = [
  { icon: Layers, title: "Tudo num só radar", desc: "Os anúncios de vários sites reunidos num único painel. Acaba o saltar entre separadores e janelas." },
  { icon: Sparkles, title: "Pesquisa por IA", desc: "Descreva por palavras o que procura — a IA lê as descrições e devolve um ranking de relevância." },
  { icon: Copy, title: "Deteção de duplicados", desc: "O telefone é o ID único: o mesmo número espalhado por vários anúncios e sites é agrupado automaticamente." },
  { icon: MessageSquareQuote, title: "Comentários agregados", desc: "As avaliações públicas dos sites, reunidas por número — a reputação de cada anúncio num só lugar." },
  { icon: BellRing, title: "Alertas diários", desc: "Saiba quem é novo, que números apareceram e quem reapareceu, com recolha automática todos os dias." },
  { icon: GitCompareArrows, title: "Comparação de perfis", desc: "Compare lado a lado e detete inconsistências (idade, cidade, fotos) que denunciam dados falsos." },
  { icon: History, title: "Histórico de cada anúncio", desc: "A linha do tempo de cada perfil: quando apareceu, desapareceu e reapareceu — sem nada se perder." },
  { icon: Star, title: "Favoritos e filtros guardados", desc: "Guarde os perfis que interessam e os seus filtros preferidos, prontos a aplicar assim que entra." },
  { icon: MessageCircle, title: "Contacto rápido", desc: "Fale por WhatsApp com uma mensagem sua predefinida, a um toque de distância do anúncio." },
  { icon: Lock, title: "Discreto e privado", desc: "Acesso restrito, sessão encriptada e planos por cidade. Vê apenas o que precisa, com discrição." },
];

// Bespoke "radar node" icon frame: concentric rings + crosshair + a soft blip,
// with a thin-line icon centred. Gives each advantage a branded, non-template look.
function RadarNode({ icon: Icon, pulse = false }) {
  return (
    <div className="relative w-14 h-14 shrink-0">
      {pulse && (
        <span className="absolute inset-0 rounded-full border border-[#c8aa6e]/40 radar-pulse" />
      )}
      <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full text-[#c8aa6e]" aria-hidden="true">
        <circle cx="28" cy="28" r="26" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="1" />
        <circle cx="28" cy="28" r="18" fill="none" stroke="currentColor" strokeOpacity="0.22" strokeWidth="1" />
        <circle cx="28" cy="28" r="10.5" fill="none" stroke="currentColor" strokeOpacity="0.32" strokeWidth="1" />
        {/* crosshair ticks */}
        <g stroke="currentColor" strokeOpacity="0.45" strokeWidth="1">
          <line x1="28" y1="1.5" x2="28" y2="6" />
          <line x1="28" y1="50" x2="28" y2="54.5" />
          <line x1="1.5" y1="28" x2="6" y2="28" />
          <line x1="50" y1="28" x2="54.5" y2="28" />
        </g>
        {/* radar blip */}
        <circle cx="41" cy="17" r="1.6" fill="currentColor" fillOpacity="0.9" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="w-[22px] h-[22px] text-[#c8aa6e]" strokeWidth={1.25} />
      </div>
    </div>
  );
}

function Logo() {
  return <img src="/radarx-logo.png" alt="RadarX" className="h-9 w-auto" />;
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#09090b] text-[#fafafa]" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur bg-[#09090b]/80 border-b border-[#18181b]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login" className="text-sm text-[#a1a1aa] hover:text-[#fafafa] px-3 py-2 transition-colors">
              Entrar
            </Link>
            <Link to="/register" data-testid="landing-register" className="text-sm font-medium bg-[#c8aa6e] text-[#09090b] px-4 py-2 hover:bg-[#d4b982] transition-colors inline-flex items-center gap-1.5">
              Começar grátis <ArrowRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#18181b]">
        {/* discreet, heavily blurred abstract backdrop — no people */}
        <img src={HERO_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.14] blur-[2px] scale-105" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/80 via-[#09090b]/90 to-[#09090b]" />
        {/* rotating radar sweep, subtle */}
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full radar-sweep opacity-70 pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 rounded-full border border-[#c8aa6e]/10" />
          <div className="absolute inset-[120px] rounded-full border border-[#c8aa6e]/10" />
          <div className="absolute inset-[240px] rounded-full border border-[#c8aa6e]/10" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-24 sm:py-32 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#c8aa6e] border border-[#c8aa6e]/30 px-3 py-1 mb-6">
            <Radar className="w-3.5 h-3.5" /> Inteligência de anúncios
          </span>
          <h1 className="font-head text-4xl sm:text-6xl font-light leading-tight max-w-3xl mx-auto">
            Toda a informação dos anúncios.<br />
            <span className="text-[#c8aa6e]">Reunida, organizada e discreta.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#a1a1aa] max-w-2xl mx-auto leading-relaxed">
            O RadarX recolhe todos os dias os perfis de vários sites, deteta números e fotos repetidos,
            agrega comentários e avisa-o das novidades — para decidir em segundos, com privacidade.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register" data-testid="hero-cta" className="bg-[#c8aa6e] text-[#09090b] font-medium px-6 py-3 hover:bg-[#d4b982] transition-colors inline-flex items-center gap-2">
              Começar agora <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="border border-[#3f3f46] text-[#e4e4e7] px-6 py-3 hover:border-[#c8aa6e] hover:text-[#c8aa6e] transition-colors">
              Já tenho conta
            </Link>
          </div>
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[#71717a]">
            <li className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#c8aa6e]" /> 7 dias grátis, sem compromisso</li>
            <li className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#c8aa6e]" /> Sessão encriptada</li>
            <li className="inline-flex items-center gap-1.5"><Radar className="w-3.5 h-3.5 text-[#c8aa6e]" /> 7 sites · 3000+ perfis</li>
          </ul>
        </div>
      </section>

      {/* Advantages — editorial 2-column layout with bespoke radar icons */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <span className="label-tiny text-[#c8aa6e]">Vantagens</span>
          <h2 className="font-head text-3xl sm:text-4xl font-light mt-2">Tudo o que ganha com o RadarX</h2>
          <p className="text-[#71717a] mt-3 text-sm leading-relaxed">
            Menos tempo a saltar entre sites, mais clareza sobre quem é quem. Cada função foi pensada
            para lhe dar respostas rápidas — com discrição do princípio ao fim.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9" data-testid="landing-advantages">
          {ADVANTAGES.map((a, i) => (
            <div key={a.title} data-testid={`advantage-${i}`} className="group flex items-start gap-5">
              <RadarNode icon={a.icon} pulse={i === 0} />
              <div className="min-w-0 pt-0.5">
                <h3 className="font-head text-lg font-normal mb-1.5 group-hover:text-[#c8aa6e] transition-colors">{a.title}</h3>
                <p className="text-sm text-[#a1a1aa] leading-relaxed">{a.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-[#18181b] bg-[#0b0b0d]">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="label-tiny">Como funciona</span>
            <h2 className="font-head text-3xl font-light mt-1">Três passos, todos os dias</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            {[
              { n: "1", t: "Recolhemos", d: "Os sites são percorridos automaticamente todos os dias." },
              { n: "2", t: "Organizamos", d: "Agrupamos por número, cidade e site; detetamos duplicados e agregamos comentários." },
              { n: "3", t: "Avisamos", d: "Recebe alertas de novidades e compara perfis quando quiser." },
            ].map((step) => (
              <div key={step.n} className="relative">
                <div className="relative w-16 h-16 mx-auto mb-5">
                  <svg viewBox="0 0 64 64" className="absolute inset-0 w-full h-full text-[#c8aa6e]" aria-hidden="true">
                    <circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeOpacity="0.18" />
                    <circle cx="32" cy="32" r="21" fill="none" stroke="currentColor" strokeOpacity="0.3" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center font-head text-2xl text-[#c8aa6e]">{step.n}</div>
                </div>
                <h3 className="font-head text-lg mb-1">{step.t}</h3>
                <p className="text-sm text-[#a1a1aa] leading-relaxed">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Monitored sites */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <span className="label-tiny">Cobertura atual</span>
          <h2 className="font-head text-3xl font-light mt-1">Sites que monitorizamos</h2>
          <p className="text-[#71717a] mt-2 text-sm">Recolha e acompanhamento diário destas fontes:</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4" data-testid="landing-sites">
          {SITES.map((s) => (
            <div
              key={s.name}
              className="group flex items-center gap-2.5 bg-[#111114] border border-[#27272a] px-5 py-3 hover:border-[#c8aa6e]/50 transition-colors"
              style={{ borderLeft: `3px solid ${s.color}` }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="font-head text-lg font-normal tracking-wide" style={{ color: s.color }}>
                {s.name}
              </span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#52525b] mt-8">
          Mais de 3000 perfis monitorizados · novas fontes adicionadas regularmente
        </p>
      </section>

      {/* Discretion / privacy band */}
      <section className="border-y border-[#18181b] bg-gradient-to-b from-[#0d0d0f] to-[#09090b]">
        <div className="max-w-4xl mx-auto px-6 py-16 flex flex-col sm:flex-row items-center gap-8 text-center sm:text-left">
          <RadarNode icon={ShieldCheck} pulse />
          <div>
            <h2 className="font-head text-2xl sm:text-3xl font-light">Feito para ser discreto</h2>
            <p className="text-sm text-[#a1a1aa] mt-3 leading-relaxed">
              Acesso restrito por subscrição, ligação encriptada (HTTPS) e planos por cidade — vê apenas
              o que precisa. O RadarX é uma ferramenta de organização de informação pública, pensada para
              lhe poupar tempo sem chamar a atenção.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#18181b]">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-head text-3xl sm:text-4xl font-light">Comece a monitorizar hoje.</h2>
          <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-[#a1a1aa]">
            <li className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#c8aa6e]" /> 7 dias grátis</li>
            <li className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#c8aa6e]" /> Sem cartão para começar</li>
            <li className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#c8aa6e]" /> Planos por cidade</li>
          </ul>
          <div className="mt-8">
            <Link to="/register" className="bg-[#c8aa6e] text-[#09090b] font-medium px-8 py-3.5 hover:bg-[#d4b982] transition-colors inline-flex items-center gap-2">
              Criar a minha conta <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#18181b]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo />
          <p className="text-xs text-[#52525b]">© {new Date().getFullYear()} RadarX · Inteligência de anúncios · Acesso restrito</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/login" className="text-[#a1a1aa] hover:text-[#c8aa6e]">Entrar</Link>
            <Link to="/register" className="text-[#a1a1aa] hover:text-[#c8aa6e]">Criar conta</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
